import { AuthService } from './authService';
import { ItemUnitsDto } from '../types/itemUnits';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7141/api/v1';

class ItemUnitsService {
  
  async getCurrentItemUnits(): Promise<ItemUnitsDto[]> {
    const response = await AuthService.authenticatedGet(`${API_BASE_URL}/item-units/current`);
    
    if (!response.ok) {
      throw new Error(`Failed to get current item units: ${response.status}`);
    }
    
    return await response.json();
  }

  async getItemUnitsByCompany(companyNo: string): Promise<ItemUnitsDto[]> {
    const response = await AuthService.authenticatedGet(`${API_BASE_URL}/item-units/company/${companyNo}`);
    
    if (!response.ok) {
      throw new Error(`Failed to get item units for company ${companyNo}: ${response.status}`);
    }
    
    return await response.json();
  }

  async getItemUnitsByCompanyAndItem(companyNo: string, itemNo: string): Promise<ItemUnitsDto[]> {
    const response = await AuthService.authenticatedGet(`${API_BASE_URL}/item-units/company/${companyNo}/item/${itemNo}`);
    
    if (!response.ok) {
      throw new Error(`Failed to get item units for company ${companyNo} and item ${itemNo}: ${response.status}`);
    }
    
    return await response.json();
  }

  async getItemUnitsByCompanyAndBarcode(companyNo: string, itemBarCode: string): Promise<ItemUnitsDto> {
    const response = await AuthService.authenticatedGet(`${API_BASE_URL}/item-units/${companyNo}/${itemBarCode}`);
    
    if (!response.ok) {
      throw new Error(`Failed to get item units for company ${companyNo} and barcode ${itemBarCode}: ${response.status}`);
    }
    
    return await response.json();
  }

  async searchByBarcode(barcode: string): Promise<ItemUnitsDto[]> {
    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/item-units/barcode/current?barcode=${encodeURIComponent(barcode)}`
    );
    
    if (!response.ok) {
      throw new Error(`Failed to search item units by barcode: ${response.status}`);
    }
    
    return await response.json();
  }

  /**
   * Eksakt oppslag av enheten som eier en skannet strekkode (inkl. GTIN-13/14-varianter).
   * Brukes av D-pak-flyten i plukk for å hente unitFactor når etiketten ikke oppgir antall.
   * Returnerer null når koden ikke er registrert som alternativ enhet — det er en normal
   * situasjon, ikke en feil, og kalleren spør da operatøren om antall i stedet.
   */
  async resolveByBarcode(barcode: string): Promise<ItemUnitsDto | null> {
    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/item-units/resolve/current?barcode=${encodeURIComponent(barcode)}`
    );

    if (response.status === 404) {
      return null;
    }

    if (!response.ok) {
      throw new Error(`Failed to resolve item unit by barcode: ${response.status}`);
    }

    return await response.json();
  }

  async searchByBarcodeInCompany(companyNo: string, barcode: string): Promise<ItemUnitsDto[]> {
    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/item-units/barcode/${companyNo}?barcode=${encodeURIComponent(barcode)}`
    );

    if (!response.ok) {
      throw new Error(`Failed to search item units by barcode in company: ${response.status}`);
    }

    return await response.json();
  }

  /**
   * Registrer ny alternativ strekkode på en vare (TW 36320099). Backend normaliserer og
   * håndhever unikhet på tvers av Items + ItemUnits (409 med forklarende melding ved duplikat).
   */
  async createItemUnit(request: {
    companyNo: string;
    itemNo: string;
    itemUnitCode: string;
    itemUnitTxt?: string | null;
    itemBarCode: string;
    unitFactor?: number | null;
  }): Promise<ItemUnitsDto> {
    const response = await AuthService.authenticatedPost(`${API_BASE_URL}/item-units`, request);

    if (!response.ok) {
      const body = await response.json().catch(() => null);
      throw new Error(body?.error || `Failed to create item unit: ${response.status}`);
    }

    return await response.json();
  }

  /** Slett en alternativ strekkode (ItemUnits-rad) identifisert ved strekkoden. */
  async deleteItemUnit(companyNo: string, itemBarCode: string): Promise<void> {
    const response = await AuthService.authenticatedDelete(
      `${API_BASE_URL}/item-units/${encodeURIComponent(companyNo)}/${encodeURIComponent(itemBarCode)}`
    );

    if (!response.ok) {
      const body = await response.json().catch(() => null);
      throw new Error(body?.error || `Failed to delete item unit: ${response.status}`);
    }
  }
}

export const itemUnitsService = new ItemUnitsService();
